import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { getFirebaseToken } from "../utils/getFirebaseToken";
import InventoryBalanceTable from "../components/InventoryBalanceTable";
import InventoryLedgerTable from "../components/InventoryLedgerTable";
import {
  fetchInventoryBalances,
  fetchInventoryLedger,
} from "../services/erpInventory";

export default function InventoryDashboardPage() {
  const location = useLocation();
const params = new URLSearchParams(location.search);
const resourceItemId = params.get("resource_item_id");
  const [rows, setRows] = useState([]);
  const [ledgerRows, setLedgerRows] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);

  const [loading, setLoading] = useState(false);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [warehouseFilter, setWarehouseFilter] = useState("");
  const [locationFilter, setLocationFilter] = useState("");

  

  const loadBalances = async () => {
    setLoading(true);
    setError("");
    try {
      const token = await getFirebaseToken();
      const data = await fetchInventoryBalances(token);
      setRows(data.rows || data.data || data || []);
    } catch (e) {
      console.error(e);
      setError(
        e?.response?.data?.message || e.message || "Failed to load inventory balances."
      );
    } finally {
      setLoading(false);
    }
  };

  const loadLedger = async (row) => {
    if (!row) return;

    setLedgerLoading(true);
    try {
      const token = await getFirebaseToken();
      const data = await fetchInventoryLedger(token, {
        resource_item_id: row.resource_item_id,
        warehouse_id: row.warehouse_id,
        location_id: row.location_id,
      });

      setLedgerRows(data.rows || data.data || data || []);
      setSelectedRow(row);
    } catch (e) {
      console.error(e);
      setError(
        e?.response?.data?.message || e.message || "Failed to load inventory ledger."
      );
    } finally {
      setLedgerLoading(false);
    }
  };

  useEffect(() => {
    loadBalances();
  }, []);

  const warehouseOptions = useMemo(() => {
    const map = new Map();
    rows.forEach((row) => {
      if (row.warehouse_id) {
        map.set(
          String(row.warehouse_id),
          row.warehouse_name || `Warehouse ${row.warehouse_id}`
        );
      }
    });
    return Array.from(map.entries()).map(([value, label]) => ({ value, label }));
  }, [rows]);

  const locationOptions = useMemo(() => {
    const map = new Map();
    rows.forEach((row) => {
      if (row.location_id) {
        map.set(
          String(row.location_id),
          row.location_name || `Location ${row.location_id}`
        );
      }
    });
    return Array.from(map.entries()).map(([value, label]) => ({ value, label }));
  }, [rows]);

  const filteredRows = useMemo(() => {
  const q = search.trim().toLowerCase();

  let baseRows = rows;

  // 🔥 OVO JE NOVO
  if (resourceItemId) {
    baseRows = baseRows.filter(
      (r) => String(r.resource_item_id) === String(resourceItemId)
    );
  }

  return baseRows.filter((row) => {
    const resource = String(
      row.resource_name || row.resource_item_name || ""
    ).toLowerCase();
    const warehouse = String(row.warehouse_id || "");
    const location = String(row.location_id || "");

    const searchOk =
      !q ||
      resource.includes(q) ||
      String(row.warehouse_name || "").toLowerCase().includes(q) ||
      String(row.location_name || "").toLowerCase().includes(q);

    const warehouseOk = !warehouseFilter || warehouse === warehouseFilter;
    const locationOk = !locationFilter || location === locationFilter;

    return searchOk && warehouseOk && locationOk;
  });
}, [rows, search, warehouseFilter, locationFilter, resourceItemId]);

  const kpis = useMemo(() => {
    const totalRows = filteredRows.length;
    const totalQty = filteredRows.reduce(
      (sum, row) => sum + Number(row.qty_on_hand || 0),
      0
    );
    const totalValue = filteredRows.reduce(
      (sum, row) => sum + Number(row.amount_on_hand || 0),
      0
    );
    const warehousesInUse = new Set(
      filteredRows.map((row) => row.warehouse_id).filter(Boolean)
    ).size;

    return {
      totalRows,
      totalQty,
      totalValue,
      warehousesInUse,
    };
  }, [filteredRows]);

  const lowStockCount = filteredRows.filter(
  (row) => Number(row.is_low_stock) === 1
).length;

  return (
    <div className="p-4 md:p-6 space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">ERP Inventory</h1>
          <p className="text-slate-600">
            Current stock balances and movement history.
          </p>
        </div>

        <button
          type="button"
          onClick={loadBalances}
          className="px-4 py-2 rounded-lg bg-black text-white"
        >
          Refresh
        </button>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <div className="bg-white rounded-xl shadow-sm border p-4">
          <div className="text-sm text-slate-500">Stock Rows</div>
          <div className="text-2xl font-bold">{kpis.totalRows}</div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border p-4">
          <div className="text-sm text-slate-500">Total Quantity</div>
          <div className="text-2xl font-bold">{kpis.totalQty.toFixed(4)}</div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border p-4">
  <div className="text-sm text-slate-500">Low Stock Items</div>
  <div className="text-2xl font-bold">{lowStockCount}</div>
</div>

        <div className="bg-white rounded-xl shadow-sm border p-4">
          <div className="text-sm text-slate-500">Total Stock Value</div>
          <div className="text-2xl font-bold">{kpis.totalValue.toFixed(2)} RSD</div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border p-4">
          <div className="text-sm text-slate-500">Warehouses in Use</div>
          <div className="text-2xl font-bold">{kpis.warehousesInUse}</div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border p-4">
        <div className="grid gap-3 md:grid-cols-4">
          <input
            type="text"
            placeholder="Search resource, warehouse, location..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="px-3 py-2 border rounded-lg"
          />

          <select
            value={warehouseFilter}
            onChange={(e) => setWarehouseFilter(e.target.value)}
            className="px-3 py-2 border rounded-lg"
          >
            <option value="">All warehouses</option>
            {warehouseOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>

          <select
            value={locationFilter}
            onChange={(e) => setLocationFilter(e.target.value)}
            className="px-3 py-2 border rounded-lg"
          >
            <option value="">All locations</option>
            {locationOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>

          <button
            type="button"
            onClick={() => {
              setSearch("");
              setWarehouseFilter("");
              setLocationFilter("");
            }}
            className="px-4 py-2 rounded-lg border"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {resourceItemId && (
  <div className="mb-4 p-3 bg-blue-50 border rounded">
    Showing stock for resource ID: {resourceItemId}
  </div>
)}

      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 text-red-700 px-4 py-3">
          {error}
        </div>
      ) : null}

      

      {loading ? (
        <div className="bg-white rounded-xl shadow-sm border p-6">Loading...</div>
      ) : (
        <InventoryBalanceTable rows={filteredRows} onViewLedger={loadLedger} />
      )}

      <div className="space-y-3">
        <div>
          <h2 className="text-lg font-semibold">
            {selectedRow
              ? `Ledger for ${selectedRow.resource_name || selectedRow.resource_item_id}`
              : "Ledger"}
          </h2>
          <p className="text-sm text-slate-500">
            {selectedRow
              ? `Warehouse: ${selectedRow.warehouse_name || selectedRow.warehouse_id}, Location: ${
                  selectedRow.location_name || selectedRow.location_id
                }`
              : "Select a balance row to inspect movement history."}
          </p>
        </div>

        {ledgerLoading ? (
          <div className="bg-white rounded-xl shadow-sm border p-6">Loading ledger...</div>
        ) : (
          <InventoryLedgerTable rows={ledgerRows} />
        )}
      </div>
    </div>
  );
}